import React from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

import {
    groupNotificationPreferences,
    useNotificationPreferences,
    useUpdateNotificationPreference,
} from './notification-preference-queries';

export const NotificationPreferences = () => {
    const { data: preferences = [], isLoading } = useNotificationPreferences();
    const { mutate: updatePreference, isPending } = useUpdateNotificationPreference();

    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center justify-center p-12 text-sm">
                Loading preferences...
            </div>
        );
    }

    const notificationSections = groupNotificationPreferences(preferences);

    const updateSectionPreferences = (sectionPreferences: typeof preferences, enabled: boolean) => {
        sectionPreferences
            .filter((preference) => preference.enabled !== enabled)
            .forEach(({ trigger }) => updatePreference({ trigger, enabled }));
    };

    const renderPreferenceRow = ({ trigger, description, enabled }: (typeof preferences)[number]) => {
        const switchId = `notification-${trigger}`;
        const togglePreference = () => {
            if (!isPending) {
                updatePreference({ trigger, enabled: !enabled });
            }
        };

        return (
            <div
                className="hover:bg-cocoa-0/60 flex min-h-11 cursor-pointer items-center justify-between gap-6 px-6 py-3.5 transition-colors"
                onClick={togglePreference}
            >
                <Label className="text-foreground min-w-0 cursor-pointer leading-6 font-normal">{description}</Label>
                <Switch
                    id={switchId}
                    checked={enabled}
                    disabled={isPending}
                    aria-label={description}
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={(checked) => updatePreference({ trigger, enabled: checked })}
                />
            </div>
        );
    };

    return (
        <div className="flex max-w-5xl flex-col gap-4">
            {notificationSections.map((section) => {
                const sectionSwitchId = `notification-section-${section.id}`;
                const isSectionEnabled =
                    section.preferences.length > 0 && section.preferences.every(({ enabled }) => enabled);
                const sectionSwitchLabel = section.preferences.length
                    ? isSectionEnabled
                        ? 'Turn all off'
                        : 'Turn all on'
                    : 'No options';
                const toggleSectionPreferences = () => {
                    if (!isPending && section.preferences.length > 0) {
                        updateSectionPreferences(section.preferences, !isSectionEnabled);
                    }
                };

                return (
                    <Card key={section.id}>
                        <CardContent className="p-0">
                            <section>
                                <div
                                    className="hover:bg-cocoa-0/60 flex cursor-pointer items-center justify-between gap-6 px-6 py-5 transition-colors"
                                    onClick={toggleSectionPreferences}
                                >
                                    <div className="min-w-0">
                                        <h2 className="text-foreground text-lg font-semibold">{section.title}</h2>
                                        <p className="text-muted-foreground mt-1 text-sm">{section.description}</p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-3">
                                        <Label className="text-muted-foreground cursor-pointer font-normal">
                                            {sectionSwitchLabel}
                                        </Label>
                                        <Switch
                                            id={sectionSwitchId}
                                            checked={isSectionEnabled}
                                            disabled={isPending || section.preferences.length === 0}
                                            aria-label={sectionSwitchLabel}
                                            onClick={(event) => event.stopPropagation()}
                                            onCheckedChange={(checked) =>
                                                updateSectionPreferences(section.preferences, checked)
                                            }
                                        />
                                    </div>
                                </div>
                                <Separator />
                                {section.preferences.length ? (
                                    section.preferences.map((preference, index) => (
                                        <React.Fragment key={preference.trigger}>
                                            {index > 0 && <Separator />}
                                            {renderPreferenceRow(preference)}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <div className="text-muted-foreground px-6 py-5 text-sm">
                                        No notification preferences available.
                                    </div>
                                )}
                            </section>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};
